<template>
  <div class="w4m-indicators">
    <Loading
      :active.sync="loading"
      :isFullPage="false"
      :z-index="500"
      :width="45"
      :height="45"
    ></Loading>
    <div class="indicator-main">
      <div
        v-for="(indicator, index) in normalizedIndicators"
        :key="getIndicatorKey(indicator, index)"
        :class="['indicator', { 'is-loading': loading }]"
        role="button"
        tabindex="0"
        :aria-label="getIndicatorAriaLabel(indicator)"
        @click="indicatorClick(index, indicator.title)"
        @keydown.enter.prevent="indicatorClick(index, indicator.title)"
        @keydown.space.prevent="indicatorClick(index, indicator.title)"
        v-tooltip="indicator.tooltip || ''"
      >
        <span class="indicator-text">
          {{ indicator.title }}
          <span
            v-if="indicator.icon"
            :class="indicator.icon"
            :style="{ color: indicator.iconColor || '' }"
          />
        </span>
        <span
          :style="getCounterStyle(indicator)"
          :class="['indicator-value', getCounterLevelClass(indicator)]"
        >
          {{ indicator.counter }}
        </span>
      </div>
    </div>
  </div>
</template>
<script>
import chroma from "chroma-js";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  name: "W4MstreakIndicators",
  components: {
    Loading,
  },
  props: {
    indicators: {
      type: Array,
      default: () => [],
    },
    loader: {
      type: Boolean,
      default: false,
    },
    warningThreshold: {
      type: Number,
      default: 20,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    normalizedIndicators() {
      return Array.isArray(this.indicators)
        ? this.indicators.map((indicator) => ({ ...indicator }))
        : [];
    },
  },
  watch: {
    loader: {
      immediate: true,
      handler(newValue) {
        this.loading = Boolean(newValue);
      },
    },
  },
  methods: {
    indicatorClick(indicatorIndex, indicatorName) {
      if (this.loading) {
        return;
      }
      return this.$emit("W4MstreakIndicatorsClick", {
        indicatorIndex,
        indicatorName,
      });
    },
    getIndicatorKey(indicator, index) {
      if (indicator && indicator.id !== undefined && indicator.id !== null) {
        return `indicator-id-${indicator.id}`;
      }
      if (indicator && indicator.title) {
        return `indicator-title-${indicator.title}-${index}`;
      }
      return `indicator-${index}`;
    },
    getIndicatorAriaLabel(indicator) {
      const title = indicator && indicator.title ? indicator.title : "Indicador";
      const counter =
        indicator && indicator.counter !== undefined && indicator.counter !== null
          ? indicator.counter
          : 0;
      return `${title}: ${counter}`;
    },
    getCounterValue(indicator) {
      const counter = Number(indicator ? indicator.counter : 0);
      if (Number.isFinite(counter)) {
        return counter;
      }
      return 0;
    },
    getCounterLevelClass(indicator) {
      const counter = this.getCounterValue(indicator);
      if (counter <= 0) {
        return "indicator-green";
      }
      if (counter <= this.warningThreshold) {
        return "indicator-yellow";
      }
      return "indicator-red";
    },
    getCounterBackgroundColor(indicator) {
      if (indicator && indicator.color) {
        return indicator.color;
      }
      const level = this.getCounterLevelClass(indicator);
      if (level === "indicator-green") {
        return "#20d077";
      }
      if (level === "indicator-yellow") {
        return "#f9c851";
      }
      return "#ef6262";
    },
    getReadableTextColor(backgroundColor) {
      try {
        const whiteContrast = chroma.contrast(backgroundColor, "#ffffff");
        const blackContrast = chroma.contrast(backgroundColor, "#000000");
        return whiteContrast > blackContrast ? "#ffffff" : "#000000";
      } catch (error) {
        return "#000000";
      }
    },
    getCounterStyle(indicator) {
      const backgroundColor = this.getCounterBackgroundColor(indicator);
      return {
        backgroundColor,
        color:
          indicator && indicator.counterColor
            ? indicator.counterColor
            : this.getReadableTextColor(backgroundColor),
      };
    },
  },
};
</script>
<style scoped>
.w4m-indicators {
  position: relative;
  width: 100%;
  min-height: 3.2rem;
  padding: 0.1rem 0 0.35rem;
}

.indicator-main {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  column-gap: 0.5rem;
  row-gap: 0.5rem;
}

.indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 220px;
  max-width: 360px;
  padding: 0.45rem 0.65rem;
  background: linear-gradient(120deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
  outline: none;
}

.indicator:hover {
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.16);
  transform: translateY(-2px);
}

.indicator:focus-visible {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(28, 116, 255, 0.65);
}

.indicator.is-loading {
  opacity: 0.7;
  pointer-events: none;
}

.indicator-text {
  color: #1f2937;
  font-weight: 600;
  margin: 0 0.7rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.indicator-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  margin-right: 0.1rem;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.1;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.12);
}

.indicator-green {
  background-color: #20d077;
}

.indicator-red {
  background-color: #ef6262;
}

.indicator-yellow {
  background-color: #f9c851;
}

@media screen and (max-width: 768px) {
  .indicator-main {
    column-gap: 0.5rem;
    row-gap: 0.55rem;
  }

  .indicator {
    width: 100%;
    max-width: 100%;
  }
}
</style>
